/**
 * Export Service — BE-14b (`GET /api/export/{reportType}?format=`). Real backend.
 * Downloads the file under the server's name (Content-Disposition); the bytes are saved as they come (CSV, PDF, XML
 * or ZIP). On an error the body is JSON: its message is thrown.
 * Formats: pdf and csv for every report; quickbooks, zoho and tally for journal-entries only.
 */

import ApiService from '../api.service';
import { blobErrorMessage } from './clientLedger.service';

const clean = (obj) => Object.fromEntries(Object.entries(obj || {}).filter(([, v]) => v !== '' && v !== undefined && v !== null));

const EXTENSIONS = { 'application/pdf': 'pdf', 'application/zip': 'zip', 'application/xml': 'xml', 'text/csv': 'csv' };

// The server names the file (Content-Disposition); when the browser cannot read that header (another origin), the
// same name is built from the filters: {reportType}_{from}_{to}, or {reportType}_{date} for an as-of report.
// (daily-sheet: {date}_{date}; annual-pl: the year's first and last day)
const AS_OF = ['customer-advances-debtors', 'receivables-aging', 'cash-position', 'balance-sheet', 'inactive-clients'];
const ACCOUNTANT_SUFFIX = { quickbooks: '_quickbooks', zoho: '_zoho', tally: '_tally' };
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const filenameOf = (headers, reportType, format, params) => {
  const disposition = headers?.['content-disposition'] || '';
  const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  if (match) return decodeURIComponent(match[1]);
  const type = (headers?.['content-type'] || '').split(';')[0].trim();
  const ext = EXTENSIONS[type] || format;
  let dates;
  if (reportType === 'daily-sheet') dates = [params.date || today(), params.date || today()];
  else if (reportType === 'annual-pl') dates = [`${params.year}-01-01`, `${params.year}-12-31`];
  else if (AS_OF.includes(reportType)) dates = [params.to || today()];
  else dates = [params.from, params.to].filter(Boolean);
  return `${[reportType, ...dates].join('_')}${ACCOUNTANT_SUFFIX[format] || ''}.${ext}`;
};

export const exportService = {
  // params: the page's own filters (from, to, customerId, bankAccountId, status, basis, masters)
  download: async (reportType, format, params = {}) => {
    let res;
    try {
      res = await ApiService.get(`/api/export/${reportType}`, { params: clean({ format, ...params }), responseType: 'blob' });
    } catch (err) {
      throw new Error(await blobErrorMessage(err, 'The export failed'));
    }
    const href = URL.createObjectURL(res.data);
    const link = document.createElement('a');
    link.href = href;
    link.download = filenameOf(res.headers, reportType, format, params);
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(href), 60000);
  },
};

export default exportService;
